'use client'

import React, { forwardRef, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { cn } from '../lib/utils'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from './form'
import { Input } from './input'

const defaultCountryCode = '+1'

// Create a new AsYouType instance for each component

const isInputValueEmailPhone = (value: string): 'email' | 'phone' => {
  // Remove common phone formatting characters to get clean value
  const cleaned = value.replace(/[()-\s]/g, '')

  // Handle +1 country code prefix for US numbers
  const withoutCountryCode = cleaned.startsWith('+1') ? cleaned.slice(2) : cleaned

  // Only consider phone mode if we have 3+ characters and they're all digits (after removing +1)
  if (withoutCountryCode.length >= 3 && /^\d+$/.test(withoutCountryCode)) {
    return 'phone'
  }

  // Everything else is email mode (including mixed digit/letter combinations)
  return 'email'
}

const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '')

  // If phone number is pasted with country code then auto format the input field.
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  } else if (cleaned.length >= 7) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  } else if (cleaned.length >= 4) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`
  }

  return cleaned
}

interface EmailPhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
  labelText?: string
}

export const EmailPhoneInput = forwardRef<HTMLInputElement, EmailPhoneInputProps>(
  ({ name, labelText, className, ...props }, ref) => {
    const form = useFormContext()
    const [inputType, setInputType] = useState<'email' | 'phone'>('email')

    const formatValue = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      let cleanedValue = value.replace(/[()-]|\s/g, '') // removes parenthesis, spaces and dash

      // Handle +1 country code prefix for pasted numbers
      if (cleanedValue.startsWith('+1')) {
        cleanedValue = cleanedValue.slice(2) // Remove +1 prefix
      }

      if (!value) {
        form.setValue(name, '')
        form.setValue('cleanedEmailPhone', '')
        form.setValue('emailPhone', '')
        form.setValue('isPhoneNumber', false)
        setInputType('email')
        return
      }

      const detectedType = isInputValueEmailPhone(value)

      if (detectedType === 'phone') {
        // Extract only digits for phone processing
        const digitsOnly = cleanedValue.replace(/\D/g, '')

        // If we already have 10 digits, don't allow more input
        if (digitsOnly.length > 10) {
          return // Don't allow more than 10 digits
        }

        // Format phone number for 4+ digits
        if (digitsOnly.length >= 4) {
          const formattedNumber = formatPhoneNumber(digitsOnly)
          form.setValue(name, formattedNumber)
          form.setValue('emailPhone', formattedNumber)
          form.setValue('cleanedEmailPhone', digitsOnly)
        } else {
          // For 1-3 digits, show just the digits without formatting
          form.setValue(name, digitsOnly)
          form.setValue('cleanedEmailPhone', digitsOnly)
          form.setValue('emailPhone', digitsOnly)
        }
        form.setValue('countryCode', defaultCountryCode)
        form.setValue('isPhoneNumber', true)
        setInputType('phone')
      } else {
        // Email mode - allow any characters including digits
        const cleanedEmail = value.replace(/[(),:;<>]|\s/g, '').toLowerCase()
        form.setValue('cleanedEmailPhone', cleanedEmail)
        form.setValue(name, cleanedEmail)
        form.setValue('emailPhone', cleanedEmail)
        form.setValue('isPhoneNumber', false)
        setInputType('email')
      }
    }

    // Handle initial value formatting
    useEffect(() => {
      const initialValue = form.getValues(name)
      if (initialValue) {
        formatValue({ target: { value: initialValue } } as React.ChangeEvent<HTMLInputElement>)
      }
    }, [])

    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {labelText && <FormLabel>{labelText}</FormLabel>}
            <FormControl>
              <div className="relative">
                {inputType === 'phone' && (
                  <div className="absolute inset-y-0 left-0 flex items-center px-3 border-r border-input">
                    {defaultCountryCode}
                  </div>
                )}
                <Input
                  {...field}
                  {...props}
                  ref={ref}
                  type={inputType === 'phone' ? 'tel' : 'text'}
                  className={cn(inputType === 'phone' && 'pl-12', className)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formatValue(e)
                  }}
                  aria-invalid={Boolean(form.formState.errors[name])}
                  initialFocus={props.autoFocus}
                />
              </div>
            </FormControl>
            <FormMessage className="text-red-400" />
          </FormItem>
        )}
      />
    )
  }
)
